/**
 * 화면 ID ↔ 라우트 (FIELD-ARC-001 §17 · FIELD-SCR-001 v0.6 라우트 표기).
 * role: 세션 역할 가드 — "public" 은 세션 없이 열린다(입장 경로).
 */
import { NETCHECK_PATH } from "../constants/paths";

/** "org" = 기관 담당자(org_admin) 또는 운영자(operator) — FS-15-06 기관 리포트(P2-S6) */
export type RouteRole = "public" | "student" | "teacher" | "operator" | "org" | "any";

export interface ScreenRoute {
  id: string;
  path: string;
  title: string;
  phase: 1 | 2 | 3;
  role: RouteRole;
}

export const screenRoutes: readonly ScreenRoute[] = [
  { id: "FS-01", path: "/launch", title: "입장·로딩(문 A · LTI)", phase: 1, role: "public" },
  { id: "FS-01-B", path: "/sso", title: "입장·로딩(문 B · Campus SSO)", phase: 1, role: "public" },
  { id: "FS-01-02", path: "/home", title: "놀이터 입구", phase: 1, role: "student" },
  { id: "FS-01-03", path: "/avatar", title: "아바타 선택", phase: 1, role: "student" },
  { id: "FS-02", path: "/upload", title: "내 작품 올리기", phase: 1, role: "student" },
  { id: "FS-03", path: "/gallery", title: "전시관", phase: 1, role: "student" },
  { id: "FS-04", path: "/course", title: "장애물 코스", phase: 1, role: "student" },
  // PICNIC-SB-FIELD-001 §6.1(2026-10-06) — 스마트 브릭 정적 기계(놀이기구 · 오토마타)를 받침대에 묶고 관절로 별 3단계
  { id: "FS-SB", path: "/stage", title: "기구 무대", phase: 1, role: "student" },
  { id: "FS-10", path: "/me", title: "나의 발자취", phase: 1, role: "student" },
  // PICNIC-MB-FIELD-001 1단계(2026-10-05) — micro:bit 화면 읽기 · 입력(보드는 Bench 시뮬레이터에서 · 이 탭은 받기만)
  { id: "FS-MB", path: "/microbit", title: "마이크로비트 놀이터", phase: 2, role: "student" },
  // P2-S1 룸 로비(모드 화면 FS-05~07 이전 — 서버 권위 월드 · FS-C1-01/03/05 연결 상태)
  { id: "FS-ROOM", path: "/room", title: "우리 반 룸", phase: 2, role: "student" },
  { id: "FS-14", path: "/replay/:id", title: "리플레이 뷰어", phase: 1, role: "any" },
  { id: "FS-01-04", path: "/teacher", title: "교사 홈", phase: 1, role: "teacher" },
  {
    id: "FS-11-PH1",
    path: "/teacher/course/:courseId/playground",
    title: "놀이터 설정",
    phase: 1,
    role: "teacher",
  },
  // P2-S4 교사 룸 콘솔(FS-11-01 룸 제어 + 02 학생 목록·개입) · 03 매칭·팀 편성
  { id: "FS-11", path: "/teacher/room/:roomId", title: "룸 콘솔", phase: 2, role: "teacher" },
  {
    id: "FS-11-03",
    path: "/teacher/room/:roomId/teams",
    title: "매칭·팀 편성",
    phase: 2,
    role: "teacher",
  },
  // P2-S5 마무리 — 13-01 스크립트 · 07 자유 놀이터 배치(콘솔 탭) · 12 칠판(새 창 · 렌더 전용) · Deep Linking 활동 만들기
  {
    id: "FS-13-01",
    path: "/teacher/room/:roomId/script",
    title: "마무리 스크립트",
    phase: 2,
    role: "teacher",
  },
  {
    id: "FS-07-01",
    path: "/teacher/room/:roomId/sandbox",
    title: "자유 놀이터 배치",
    phase: 2,
    role: "teacher",
  },
  { id: "FS-12-01", path: "/board/:roomId", title: "칠판 뷰", phase: 2, role: "teacher" },
  // P2-S6 관찰 메모·성취(콘솔 탭 — 룸이 닫혀도 입력할 수 있다)
  {
    id: "FS-11-04",
    path: "/teacher/room/:roomId/assess",
    title: "관찰 메모·성취",
    phase: 2,
    role: "teacher",
  },
  // P3-S1 릴레이 순서 배치·진행(콘솔 탭 — SCR v0.7 검토 v07-03 · 탭은 Phase 3 플래그가 켜진 코스만)
  {
    id: "FS-08-01",
    path: "/teacher/room/:roomId/relay",
    title: "릴레이 순서 배치",
    phase: 3,
    role: "teacher",
  },
  // P3-S3 공동 작품 영상(콘솔 탭 — SCR v0.7 검토 4-4 · 룸이 닫혀도 열린다)
  {
    id: "FS-14-02",
    path: "/teacher/room/:roomId/video",
    title: "공동 작품 영상",
    phase: 3,
    role: "teacher",
  },
  // P3-S4 교류 룸 초대·수락(콘솔 탭 — SCR v0.7 검토 §4 신설 · 룸이 없어도 열린다 · 'recent' 면 룸 없이)
  {
    id: "FS-11-08",
    path: "/teacher/room/:roomId/exchange",
    title: "교류 룸 초대·수락",
    phase: 3,
    role: "teacher",
  },
  // SR-NET-05 단일 포트 · 학교망 진단(교사 · 도입 전 — FS-C1 공통 틀 · 교사 홈 바로 가기)
  { id: "FS-C1-08", path: NETCHECK_PATH, title: "학교망 진단", phase: 2, role: "teacher" },
  {
    id: "FS-11-05-DL",
    path: "/teacher/deeplink",
    title: "마무리 활동 만들기(Deep Linking)",
    phase: 2,
    role: "teacher",
  },
  // P3-S5 관찰자 진입(SCR v0.7 검토 §3.5 · 세션 없이도 열린다 — 세션 상태는 화면이 스스로 본다 · 플래그 features.observerShare)
  { id: "FS-01-05", path: "/share/:token", title: "관찰자 진입", phase: 3, role: "public" },
  {
    id: "FS-01-05-HOME",
    path: "/share",
    title: "관찰자 진입(링크 없음)",
    phase: 3,
    role: "public",
  },
  { id: "FS-15", path: "/ops", title: "운영 콘솔", phase: 1, role: "operator" },
  { id: "FS-15-01", path: "/ops/monitor", title: "룸 모니터", phase: 2, role: "operator" },
  { id: "FS-15-02", path: "/ops/catalog", title: "카탈로그 관리", phase: 1, role: "operator" },
  { id: "FS-15-03", path: "/ops/modes", title: "모드 정의·추천 매핑", phase: 1, role: "operator" },
  { id: "FS-15-04", path: "/ops/config", title: "런타임 설정", phase: 1, role: "operator" },
  { id: "FS-15-05", path: "/ops/assets", title: "자산 버전·서비스 키", phase: 1, role: "operator" },
  { id: "FS-15-07", path: "/ops/stages", title: "기구 무대 꾸밈", phase: 1, role: "operator" },
  { id: "FS-15-08", path: "/ops/goals", title: "기구 무대 목표", phase: 1, role: "operator" },
  // P2-S6 기관 비식별 집계 — 기관 담당자의 홈 · 운영자는 운영 콘솔 탭에서
  { id: "FS-15-06", path: "/org/report", title: "기관 리포트", phase: 2, role: "org" },
];

/** 라우트 역할 가드 — "org" 는 기관 담당자와 운영자 둘 다 */
export const roleAllows = (route: Exclude<RouteRole, "public">, actual: string) =>
  route === "any" ||
  route === actual ||
  (route === "org" && (actual === "org_admin" || actual === "operator"));

/** 경로 도우미는 constants/paths.ts 가 원본 — 화면 표 시험 · 기존 import 를 위해 다시 내보낸다 */
export {
  SHARE_HOME,
  sharePath,
  homeFor,
  roomConsolePath,
  boardPath,
  scriptPath,
  sandboxPath,
  assessPath,
  relayPath,
  RECENT_ROOM,
  videoPath,
  exchangePath,
  ORG_REPORT_PATH,
  playgroundPath,
  NETCHECK_PATH,
} from "../constants/paths";
